export const MOBILITY_BUTTON_KEYS = [
  'buy_ticket',
  'visio_agency',
  'bot_iv',
  'voice_feedback',
  'contact',
];

// Only these known keys require a URL to be considered "available".
// Custom buttons always require a URL (checked separately below).
const EXTERNAL_URL_KEYS = new Set(['buy_ticket', 'visio_agency', 'bot_iv']);

export const EMPTY_MOBILITY_CONFIG = {
  buttons: [],
  show_app_stores: false,
  app_store_url: '',
  google_play_url: '',
};

/**
 * Returns true when the button is a custom (admin-created) entry.
 * The backend marks these by including a `label` field and using a key
 * that is not in the built-in MOBILITY_BUTTON_KEYS list.
 */
function isCustomButton(button) {
  return !MOBILITY_BUTTON_KEYS.includes(button.key);
}

function isExternalButtonAvailable(key, url) {
  if (!EXTERNAL_URL_KEYS.has(key)) return true;
  return Boolean(url?.trim());
}

/**
 * Normalize the public API mobility payload for the UI.
 * @param {import('../api').MobilityConfig | null | undefined} apiConfig
 */
export function resolveMobilityConfig(apiConfig) {
  if (!apiConfig || !Array.isArray(apiConfig.buttons)) {
    return EMPTY_MOBILITY_CONFIG;
  }

  const buttons = apiConfig.buttons
    .filter((button) => {
      if (!button.key || typeof button.key !== 'string') return false;

      if (isCustomButton(button)) {
        // Custom buttons must have a valid URL — they are always external links.
        return Boolean(button.url?.trim());
      }

      return isExternalButtonAvailable(button.key, button.url);
    })
    .map((button) => {
      if (isCustomButton(button)) {
        return {
          key: button.key,
          // label comes from the DB via the API; fall back to the key itself.
          label: (button.label || button.key).trim(),
          url: button.url.trim(),
          // icon key (e.g. 'ShoppingCart') — used to look up the MUI component.
          icon: button.icon || '',
        };
      }

      const entry = { key: button.key };
      if (EXTERNAL_URL_KEYS.has(button.key) && button.url?.trim()) {
        entry.url = button.url.trim();
      }
      return entry;
    });

  return {
    buttons,
    show_app_stores: Boolean(apiConfig.show_app_stores),
    app_store_url: apiConfig.app_store_url?.trim() || '',
    google_play_url: apiConfig.google_play_url?.trim() || '',
  };
}

export function getEnabledMobilityButtonKeys(config) {
  return (config?.buttons || []).map((button) => button.key);
}
